import { useState } from "react";
import { useNavigate } from "react-router";
import { Button } from "../../components/Button";
import { BackButton } from "../../components/BackButton";
import { ScreenState } from "../../components/ScreenState";
import { useAsync } from "../../lib/useAsync";
import { fetchSubjects, saveOnboarding, type CatalogSubject } from "../../api/onboarding";
import { fetchMe } from "../../api/me";
import { ApiError } from "../../api/client";
import { subjectsToPickForExam } from "../../lib/catalog";

const isEnglish = (name: string) => /^(use of )?english$/i.test(name.trim());

/**
 * Edit subjects from Profile.
 *
 * Same rules as onboarding (screen 8): English is locked, pick exactly 3 more.
 * But here the student's current subjects are pre-selected so they can swap
 * one without re-picking everything.
 */
export function EditSubjectsScreen() {
  const navigate = useNavigate();
  const me = useAsync(fetchMe);
  const catalog = useAsync(
    () => fetchSubjects((me.data?.student.examType as "JAMB" | "WAEC") ?? undefined),
    [me.data?.student.examType],
  );

  const [picked, setPicked] = useState<string[] | null>(null);
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const subjects: CatalogSubject[] = catalog.data?.subjects ?? [];
  const required = subjects.filter(
    (subject) => subject.required || (!subjects.some((s) => s.required) && isEnglish(subject.name)),
  );
  const optional = subjects.filter((subject) => !required.includes(subject));

  // Pre-select current subjects on first load.
  const currentSubjectIds = me.data?.subjects.map((s) => s.subjectId) ?? [];
  const requiredIds = new Set(required.map((s) => s.id));
  const initialPicked = currentSubjectIds.filter((id) => !requiredIds.has(id));
  const selected = picked ?? initialPicked;

  const examType = me.data?.student.examType;
  const maxPick = subjectsToPickForExam(examType);
  const unlimited = maxPick === null;

  const toggle = (id: string) => {
    const current = picked ?? initialPicked;
    setPicked(
      current.includes(id)
        ? current.filter((s) => s !== id)
        : !unlimited && current.length >= maxPick
          ? current
          : [...current, id],
    );
  };

  const complete = unlimited ? selected.length >= 1 : selected.length === maxPick;

  const save = async () => {
    if (!complete || saving) return;
    setSaving(true);
    setFailure(null);

    try {
      await saveOnboarding({
        subjectIds: [...required.map((s) => s.id), ...selected],
        // The profile on screen: changing WAEC subjects leaves JAMB's alone.
        ...(examType === "JAMB" || examType === "WAEC" ? { profile: examType } : {}),
      });
      navigate(-1);
    } catch (caught) {
      setFailure(
        caught instanceof ApiError ? caught.message : "We couldn't save your subjects. Try again.",
      );
    } finally {
      setSaving(false);
    }
  };

  const loading = me.loading || catalog.loading;
  const error = me.error || catalog.error;

  return (
    <ScreenState loading={loading} error={error} onRetry={me.reload}>
      <div className="app-column px-5 pb-6 pt-6">
        <BackButton />

        <h1 className="m-0 pt-4 font-[family-name:var(--font-display)] text-[22px] font-bold">
          {(me.data?.exams?.length ?? 0) > 1
            ? `Change your ${examType} subjects`
            : "Change your subjects"}
        </h1>
        <p className="m-0 pt-2 text-[13px] leading-[1.5] text-text-muted">
          {required.map((s) => s.name).join(" and ") || "English"} is in for everyone.{" "}
          {unlimited ? "Pick as many as you're sitting." : `Pick ${maxPick} more.`}
        </p>

        <ul className="m-0 flex list-none flex-wrap gap-2 p-0 pt-5">
          {required.map((subject) => (
            <li key={subject.id}>
              <span className="flex items-center gap-2 rounded-[var(--radius-chip)] border border-accent bg-accent-tint px-3.5 py-2.5 text-[13.5px] font-semibold">
                {subject.name}
                <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-text-muted">
                  Required
                </span>
              </span>
            </li>
          ))}

          {optional.map((subject) => {
            const isPicked = selected.includes(subject.id);
            const atLimit = !unlimited && !isPicked && selected.length >= maxPick;

            return (
              <li key={subject.id}>
                <button
                  type="button"
                  aria-pressed={isPicked}
                  disabled={atLimit || saving}
                  onClick={() => toggle(subject.id)}
                  className={`min-h-11 rounded-[var(--radius-chip)] border px-3.5 py-2.5 text-[13.5px] transition-colors disabled:opacity-40
                    ${isPicked ? "border-accent bg-accent-tint font-semibold text-text" : "border-border bg-surface text-text-muted"}`}
                >
                  {subject.name}
                </button>
              </li>
            );
          })}
        </ul>

        <div className="mt-auto flex flex-col gap-3 pt-8">
          <p className="tabular m-0 text-center text-[12.5px] text-text-muted">
            {unlimited ? `${selected.length} picked` : `${selected.length} of ${maxPick} picked`}
          </p>
          {failure ? (
            <p role="alert" className="m-0 text-center text-[12.5px] text-negative">
              {failure}
            </p>
          ) : null}
          <Button disabled={!complete || saving} onClick={() => void save()}>
            {saving ? "Saving…" : "Save subjects"}
          </Button>
        </div>
      </div>
    </ScreenState>
  );
}
